import { AaveV2TypeGetUserReserveData } from '../../../types/lenderTypes'
import { createBaseTypeUserState } from '../utils'
import { parseRawAmount } from '../../../utils/parsing'
import { isFailedCall } from '../../../utils/multicall'
import {
  LenderYieldComplete,
  LenderCrossPoolMeta,
  UserData,
} from '../utils/types'
import { getDisplayPrice, getOraclePrice } from '../utils/oraclePrice'
import { createMarketUid } from '../../../utils/marketUid'
import { getAaveAssets } from '../../../assets'

export const getAaveV2UserDataConverter = (
  lender: string,
  chainId: string,
  account: string,
  metaMap?: LenderCrossPoolMeta,
): [(data: any[]) => UserData | undefined, number] => {
  switch (lender) {
    default: {
      // Use same asset ordering as build function
      const assetsToQuery = getAaveAssets(chainId, lender).sort((a, b) =>
        a.toLowerCase().localeCompare(b.toLowerCase()),
      )
      const expectedNumberOfCalls = assetsToQuery.length
      return [
        (data: any[]) => {
          if (data.length !== expectedNumberOfCalls) {
            return undefined
          }

          const lendingPositions: { [marketUid: string]: any } = {}
          let totalDebt24h = 0
          let totalDeposits24h = 0

          for (let i = 0; i < assetsToQuery.length; i++) {
            const asset = assetsToQuery[i]
            const key = createMarketUid(chainId, lender, asset)
            const metaEntity = metaMap?.[key]!
            const { dataForAsset, addedDebt, addedDeposits } =
              createAaveV2Entry(i, data, key, metaEntity, 0)
            if (!dataForAsset) continue
            totalDebt24h += addedDebt
            totalDeposits24h += addedDeposits
            lendingPositions[key] = dataForAsset
          }

          const payload = {
            chainId,
            account,
            lendingPositions,
            rewards: [],
          }
          const userData = createBaseTypeUserState(
            payload,
            metaMap!,
            totalDeposits24h,
            totalDebt24h,
          )
          return userData
        },
        expectedNumberOfCalls,
      ]
    }
  }
}

function createAaveV2Entry(
  i: number,
  data: any[],
  key: string,
  meta: LenderYieldComplete,
  claimableRewards: any,
) {
  const reserveData = data[i]

  // A failed read is not a zero balance — skip the market (see the Aave V3
  // converter for why the sentinel would otherwise pass the all-zero check).
  if (isFailedCall(reserveData)) {
    return { dataForAsset: undefined, addedDeposits: 0, addedDebt: 0 }
  }

  const currentATokenBalanceRaw =
    reserveData[AaveV2TypeGetUserReserveData.currentATokenBalance]?.toString()
  const currentStableDebtRaw =
    reserveData[AaveV2TypeGetUserReserveData.currentStableDebt]?.toString()
  const currentVariableDebtRaw =
    reserveData[AaveV2TypeGetUserReserveData.currentVariableDebt]?.toString()

  if (
    currentATokenBalanceRaw === '0' &&
    currentStableDebtRaw === '0' &&
    currentVariableDebtRaw === '0'
  ) {
    return {
      dataForAsset: undefined,
      addedDeposits: 0,
      addedDebt: 0,
    }
  }

  const assetMeta = meta.asset
  const decimals = assetMeta?.decimals ?? 18
  const currentATokenBalance = parseRawAmount(currentATokenBalanceRaw, decimals)
  const currentStableDebt = parseRawAmount(currentStableDebtRaw, decimals)
  const currentVariableDebt = parseRawAmount(currentVariableDebtRaw, decimals)
  const price = getDisplayPrice(meta)
  const oPrice = getOraclePrice(meta)
  const priceHist = meta?.price?.priceUsd24h ?? price

  const dataForAsset = {
    marketUid: key,
    underlying: assetMeta.address,
    deposits: currentATokenBalance,
    debtStable: currentStableDebt,
    debt: currentVariableDebt,
    depositsUSD: Number(currentATokenBalance) * price,
    debtStableUSD: Number(currentStableDebt) * price,
    debtUSD: Number(currentVariableDebt) * price,
    depositsUSDOracle: Number(currentATokenBalance) * oPrice,
    debtStableUSDOracle: Number(currentStableDebt) * oPrice,
    debtUSDOracle: Number(currentVariableDebt) * oPrice,
    stableBorrowRate:
      reserveData[AaveV2TypeGetUserReserveData.stableBorrowRate]?.toString(),
    collateralEnabled: Boolean(
      reserveData[AaveV2TypeGetUserReserveData.usageAsCollateralEnabled],
    ),
    claimableRewards,
  }

  return {
    dataForAsset,
    addedDeposits: Number(currentATokenBalance) * priceHist,
    addedDebt:
      (Number(currentVariableDebt) + Number(currentStableDebt)) * priceHist,
  }
}
